import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Plus, Users, MonitorPlay } from 'lucide-react';
import { cn } from '../../lib/utils';
import { DIFFICULTY_COLORS, DIFFICULTY_LABELS, STATUS_LABELS } from './combatConstants';

export function EncounterList({ 
  encounters, 
  selectedEncounter, 
  loading, 
  language, 
  onSelectEncounter, 
  onCreateClick,
  onOpenPlayerView,
  onSetPlayerViewEncounter,
  activePlayerEncounterId,
  activePlayerEncounterName
}) {
  const activeEncounterCount = encounters.filter(
    (encounter) => encounter.status === 'active' || encounter.status === 'paused'
  ).length;

  return (
    <div className="w-72 border-r border-border bg-card flex flex-col">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-cinzel font-bold text-lg">
            {language === 'fr' ? 'Rencontres' : 'Encounters'}
          </h2>
          <div className="flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              onClick={onOpenPlayerView}
              title={language === 'fr' ? 'Vue joueur' : 'Player view'}
              data-testid="open-campaign-player-view-btn"
            >
              <MonitorPlay className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" onClick={onCreateClick} data-testid="create-encounter-btn">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground truncate">
          {`affichée:${activePlayerEncounterName || '-'}`}
        </p>
      </div>
      
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-2">
          {loading ? (
            [1, 2, 3].map(i => (
              <Card key={i} className="glass-card">
                <CardContent className="p-3">
                  <div className="animate-pulse space-y-2">
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </div>
                </CardContent>
              </Card>
            ))
          ) : encounters.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              {language === 'fr' ? 'Aucune rencontre' : 'No encounters'}
            </p>
          ) : (
            encounters.map(encounter => (
              <Card 
                key={encounter.id}
                data-testid={`encounter-card-${encounter.id}`}
                className={cn(
                  "cursor-pointer transition-all hover:bg-secondary/50",
                  selectedEncounter?.id === encounter.id && "ring-2 ring-primary bg-secondary/30"
                )}
                onClick={() => onSelectEncounter(encounter)}
              >
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm truncate">{encounter.name}</h3>
                      <p className="text-xs text-muted-foreground truncate">
                        {encounter.location || '-'}
                      </p>
                    </div>
                    <Badge 
                      variant="outline" 
                      className={cn("text-[10px] text-white shrink-0", DIFFICULTY_COLORS[encounter.difficulty])}
                    >
                      {DIFFICULTY_LABELS[language]?.[encounter.difficulty]?.[0]}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {encounter.combatants?.length || 0}
                    </span>
                    <span>{encounter.total_xp} XP</span>
                    <Badge variant="secondary" className="text-[10px] ml-auto">
                      {STATUS_LABELS[language]?.[encounter.status]?.[0] || encounter.status}
                    </Badge>
                  </div>
                  {(encounter.status === 'active' || encounter.status === 'paused') && activeEncounterCount > 1 && (
                    <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-border/60">
                      {activePlayerEncounterId === encounter.id ? (
                        <span className="text-[10px] text-primary truncate">
                          {language === 'fr' ? 'Affichee cote joueurs' : 'Displayed to players'}
                        </span>
                      ) : (
                        <span className="text-[10px] text-muted-foreground">
                          {language === 'fr' ? 'Combat actif' : 'Active combat'}
                        </span>
                      )}
                      <Button
                        type="button"
                        variant={activePlayerEncounterId === encounter.id ? 'secondary' : 'outline'}
                        size="sm"
                        className="h-7 px-2 text-[10px]"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSetPlayerViewEncounter?.(encounter.id);
                        }}
                      >
                        {language === 'fr' ? 'Afficher' : 'Display'}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
